import React, { useEffect, useState } from 'react';
import useAuthOktaUser from '../../../../app/hooks/useAuthOktaUser';
import { Grid, InputAdornment, TextField, Box, Button, Typography, Alert } from '@mui/material';
import AppCustomIcon from '../../../../app/features/icon/AppIcon';
import styleVariables from '../../../../app/styles/styleVariables';
import { useTranslation } from 'react-i18next';
import langAuthPasswordForgot from './langs/langAuthPasswordForgot';
import * as yup from 'yup';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// https://www.ciam.ninja/post/okta-implementing-a-custom-password-reset-page-with-a-pro-code-approach

interface IAuthForgotPasswordForm {
	email: string;
}

const FAuthPasswordForgot = () => {
	const authOktaUser = useAuthOktaUser();
	const { t } = useTranslation();

	const sxTextField = {
		'& .MuiInputBase-root': {
			border: '1px solid #EEE',
		},
	};

	const [isSended, setIsSended] = useState<boolean | null>(null);

	useEffect(() => {
		return () => {
			setIsSended(null);
		};
	}, []);

	const schema = yup
		.object({
			email: yup
				.string()
				.required(t(langAuthPasswordForgot.field_error_email_required.tag))
				.email(t(langAuthPasswordForgot.field_error_email_invalid.tag)),
		})
		.required();

	const {
		handleSubmit,
		control,
		formState: { errors },
	} = useForm<IAuthForgotPasswordForm>({
		resolver: yupResolver(schema),
		defaultValues: { email: localStorage.getItem('authLogin') ?? '' },
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const onSubmit: SubmitHandler<IAuthForgotPasswordForm> = (data) => {
		setIsSubmitting(true);
		authOktaUser
			.forgotPassword({
				username: data.email,
				factorType: 'EMAIL',
			})
			.then(function (transaction) {
				if (transaction.status === 'RECOVERY_CHALLENGE') {
					setIsSended(true);
				} else {
					setIsSended(false);
				}
			})
			.catch(function () {
				setIsSended(false);
			})
			.finally(() => {
				setIsSubmitting(false);
			});
	};

	return (
		<>
			{isSended === null ? (
				<>
					<Typography variant="body2" gutterBottom sx={{ textAlign: 'left', marginBottom: '30px' }}>
						{t(langAuthPasswordForgot.text_presentation.tag)}
					</Typography>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Controller
									name="email"
									control={control}
									defaultValue=""
									render={({ field }) => (
										<TextField
											fullWidth
											sx={sxTextField}
											label={t(langAuthPasswordForgot.label_email.tag)}
											variant="outlined"
											type="text"
											size="medium"
											autoComplete="off"
											{...field}
											error={!!errors.email}
											helperText={errors?.email?.message}
											InputProps={{
												endAdornment: (
													<InputAdornment position="end">
														<AppCustomIcon
															tag="auth_login"
															sx={{ color: styleVariables.primaryColor }}
														/>
													</InputAdornment>
												),
											}}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Box style={{ textAlign: 'center' }}>
									<Button
										disabled={isSubmitting}
										type="submit"
										variant="contained"
										color="secondary"
										sx={{ minWidth: 160, fontWeight: 600 }}
										endIcon={<AppCustomIcon tag="arrow-right" />}
									>
										{t(langAuthPasswordForgot.btn_reinit_password.tag)}
									</Button>
								</Box>
							</Grid>
						</Grid>
					</form>
				</>
			) : isSended === true ? (
				<>
					<Alert severity="success" elevation={0} sx={{ boxShadow: 'none' }}>
						<Typography variant="body2" gutterBottom sx={{ textAlign: 'left' }}>
							{t(langAuthPasswordForgot.text_success.tag)}
						</Typography>
					</Alert>
				</>
			) : (
				<>
					<Alert severity="error" elevation={0} sx={{ boxShadow: 'none' }}>
						<Typography variant="body2" gutterBottom sx={{ textAlign: 'left' }}>
							{t(langAuthPasswordForgot.text_error.tag)}
						</Typography>
					</Alert>
				</>
			)}
		</>
	);
};

export default FAuthPasswordForgot;
